import React from 'react';
import {
  View, StyleSheet, Text, FlexStyle,
} from 'react-native';
import { CartesianChart, Line } from 'victory-native';
import { Circle, useFont } from '@shopify/react-native-skia';
import Colors from '../constants/Colors';
import { axisDateLabel, ChartDataPoint } from './axisUtils';
import { selectedLineColor } from './AirQualityGraph';
import { comparisonLineColor } from '../components/Home/HomeGraphLineSelector';

interface LineGraphMobileProps {
  data: ChartDataPoint[];
  comparisonData: ChartDataPoint[];
  style?: FlexStyle | FlexStyle[];
}

const LineGraphMobile = ({ data, comparisonData, style }: LineGraphMobileProps) => {
  const dataExists: boolean = !!(data?.length || comparisonData?.length);
  // eslint-disable-next-line global-require
  const font = useFont(require('../assets/fonts/Roboto-Regular.ttf'), 11);
  const maxVisibleXAxisLabels: number = 4;
  const longestLength: number = Math.max(data?.length ?? 0, comparisonData?.length ?? 0);
  const labelSlotCount: number = Math.max(1, Math.min(maxVisibleXAxisLabels, longestLength));

  const labelSlots = React.useMemo(
    () => Array.from({ length: labelSlotCount }, (_, idx) => idx),
    [labelSlotCount],
  );

  const getSlotIndexForDataset = (datasetLength: number, slot: number): number => {
    if (datasetLength <= 1 || labelSlotCount <= 1) return 0;

    return Math.round((slot / (labelSlotCount - 1)) * (datasetLength - 1));
  };

  const domainLimit: number = dataExists
    ? 1.2
      * Math.max(
        ...(data ?? []).map((point) => point.value),
        ...(comparisonData ?? []).map((point) => point.value),
        1,
      )
    : 1;

  return dataExists ? (
    <View style={[styles.container, style]}>
      <View style={styles.chartWrapper}>
        <CartesianChart
          data={data}
          xKey="date"
          yKeys={['value']}
          domain={{ y: [0, domainLimit] }}
          frame={{ lineWidth: 0 }}
          axisOptions={{
            font,
            formatXLabel: () => '',
            lineColor: Colors.light.hereworksWhite500,
            lineWidth: { grid: { x: 0, y: 1 }, frame: 0 },
          }}
          domainPadding={{
            top: 20,
            left: 16,
            right: 16,
            bottom: 12,
          }}
        >
          {({ points }) => (
            <>
              <Line
                points={points.value}
                color={selectedLineColor}
                strokeWidth={2}
                animate={{ type: 'timing', duration: 300 }}
              />
              {points.value.map((point, idx) => (
                <Circle
                  key={idx}
                  cx={point.x}
                  cy={point.y}
                  r={3}
                  color={selectedLineColor}
                />
              ))}
            </>
          )}
        </CartesianChart>

        <View style={styles.chartOverlay} pointerEvents="none">
          <CartesianChart
            data={comparisonData}
            xKey="date"
            yKeys={['value']}
            domain={{ y: [0, domainLimit] }}
            frame={{ lineWidth: 0 }}
            axisOptions={{
              font,
              tickCount: { x: 4, y: 0 },
              labelOffset: { x: 0, y: 22 },
              labelColor: Colors.light.hereworksYellow500,
              labelPosition: 'inset',
              formatXLabel: () => '',
              lineColor: Colors.light.hereworksYellow500,
              lineWidth: { grid: { x: 0, y: 0 }, frame: 0 },
            }}
            domainPadding={{
              top: 20,
              left: 16,
              right: 16,
              bottom: 12,
            }}
          >
            {({ points }) => (
              <>
              <Line
                points={points.value}
                color={comparisonLineColor}
                strokeWidth={2}
              />
              {points.value.map((point, idx) => (
                <Circle
                  key={idx}
                  cx={point.x}
                  cy={point.y}
                  r={3}
                  color={Colors.light.hereworksYellow500}
                />
              ))}
              </>
            )}
          </CartesianChart>
        </View>
      </View>
      {labelSlots.length > 0 && <>
      <View style={styles.selectedAxisRow}>
        {labelSlots.map((slot) => {
          const idx = getSlotIndexForDataset(data.length, slot);
          return <Text key={`selected_label_${slot}`} style={styles.selectedAxisLabel}>
            {data.length > 0 ? axisDateLabel(data, idx) : ''}
          </Text>;
        })}
      </View>
      <View style={styles.comparisonAxisRow}>
        {labelSlots.map((slot) => {
          const idx = getSlotIndexForDataset(comparisonData.length, slot);
          return <Text key={`comparison_label_${slot}`} style={styles.comparisonAxisLabel}>
            {comparisonData.length > 0 ? axisDateLabel(comparisonData, idx) : ''}
          </Text>;
        })}
      </View>
      </>}
    </View>
  ) : (
    <View style={[{ justifyContent: 'center', alignItems: 'center', flex: 1 }]}>
      <Text>{'No data available'}</Text>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    marginTop: 0,
    marginEnd: 0,
  },
  chartWrapper: {
    position: 'relative',
    flex: 1,
  },
  chartOverlay: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
  },
  comparisonAxisRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 2,
    paddingHorizontal: 6,
  },
  selectedAxisRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: 6,
  },
  selectedAxisLabel: {
    flex: 1,
    textAlign: 'center',
    color: Colors.light.hereworksBlack600,
    fontSize: 10,
  },
  comparisonAxisLabel: {
    flex: 1,
    textAlign: 'center',
    color: Colors.light.hereworksYellow500,
    fontSize: 10,
  },
});

export default LineGraphMobile;
